import React, { Suspense } from 'react'
import { Switch, Route, NotFound } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useEffectiveUser } from '@fs/zion-user'

import UnauthenticatedRoutes from './pages/PortalPage/UnauthenticatedPortalPage/UnauthenticatedPortalPage'
import AuthWithFallbackRoute from './shared/components/AuthWithFallbackRoute'
import QueryClientProviderWrapper from './QueryClientProviderWrapper'
import ErrorBoundaryFallback from './shared/components/ErrorBoundaryFallback/ErrorBoundaryFallback'
import TreeBuilderAbTest from './shared/components/TreeBuilderAbTest/TreeBuilderAbTest'
import { africaSupportedCountryNames } from './team-spaces/africa/africaCountries'
import { ZION_CMS_SITES } from './constants'

const PortalPage = React.lazy(() => import('./pages/PortalPage'))
const FamilyFeedPage = React.lazy(() => import('./pages/FamilyFeedPage'))
const UnauthenticatedFamilyFeedPage = React.lazy(() => import('./pages/FamilyFeedPage/UnauthenticatedFamilyFeedPage'))
const FirstAncestorBanner = React.lazy(() => import('./FirstAncestorBanner'))
const BrightspotRouting = React.lazy(() =>
  import('@fs/zion-cms').then((module) => ({ default: module.BrightspotRouting }))
)

// The tree-builder A/B test has to be decided after sign-in but before PortalPage mounts, so it wraps
// the portal route's component rather than living inside the page.
function PortalRoute() {
  return (
    <TreeBuilderAbTest>
      <PortalPage />
    </TreeBuilderAbTest>
  )
}

function App() {
  const { signedIn } = useEffectiveUser()
  // this is for convenience for testing languages quickly locally. (Optional param cause it's not needed in production because the locale is part of the basename given to route).
  const localePath = '([a-z]{2,3}|[a-z]{2,3}-[a-zA-Z0-9-]*)?'
  const homePortalPath = '(home/portal)?'
  // capture these NLIHP routes so when logged in it will redirect to remove these
  const nlihpPaths = `(chinese|hong-kong|macau|mainland-china|singapore|taiwan|united-states|canada|${africaSupportedCountryNames.join('|')})?`
  const portalPath = `/${localePath}/${homePortalPath}/${nlihpPaths}`
  const familyFeedPath = `/${localePath}/feed`
  const zionCMSPath = `/${localePath}/:site(${ZION_CMS_SITES.join('|')})`

  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <QueryClientProviderWrapper>
        {signedIn && (
          <Suspense fallback={null}>
            <FirstAncestorBanner />
          </Suspense>
        )}
        <Switch>
          <AuthWithFallbackRoute exact path={portalPath} component={PortalRoute} fallback={UnauthenticatedRoutes} />
          <AuthWithFallbackRoute
            exact
            requirePersonalData
            path={familyFeedPath}
            component={FamilyFeedPage}
            fallback={UnauthenticatedFamilyFeedPage}
          />
          <Route path={zionCMSPath} render={({ match }) => <BrightspotRouting site={match.params.site} />} />
          <Route component={NotFound} />
        </Switch>
      </QueryClientProviderWrapper>
    </ErrorBoundary>
  )
}
export default App
